import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { settingsApi } from "../../api/settings";

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [intervalDays, setIntervalDays] = useState<number>(7);
  const [maxReminders, setMaxReminders] = useState<number>(3);
  const [retentionDays, setRetentionDays] = useState<number>(90);
  const [tokenExpirationDays, setTokenExpirationDays] = useState<number>(30);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: settingsApi.getSettings,
  });

  useEffect(() => {
    if (settings) {
      setIntervalDays(settings.default_reminder_interval_days);
      setMaxReminders(settings.max_reminders_allowed);
      setRetentionDays(settings.campaign_retention_days);
      setTokenExpirationDays(settings.token_expiration_days);
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: settingsApi.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setMessage({ type: "success", text: "Settings updated successfully!" });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to update settings";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.detail || errorMessage;
      }
      setMessage({
        type: "error",
        text: errorMessage,
      });
      setTimeout(() => setMessage(null), 3000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (retentionDays <= 0) {
      setMessage({
        type: "error",
        text: "Campaign retention days must be a positive number",
      });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    updateMutation.mutate({
      default_reminder_interval_days: intervalDays,
      max_reminders_allowed: maxReminders,
      campaign_retention_days: retentionDays,
      token_expiration_days: tokenExpirationDays,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-primary">Naef</h1>
              <span className="ml-4 text-gray-600">Reminder Settings</span>
            </div>
            <button onClick={() => navigate("/admin/dashboard")} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card">
          <h2 className="text-2xl font-bold mb-6">Configure Automatic Reminders</h2>

          {message && (
            <div
              className={`mb-4 px-4 py-3 rounded ${
                message.type === "success"
                  ? "bg-green-100 border border-green-400 text-green-700"
                  : "bg-red-100 border border-red-400 text-red-700"
              }`}
            >
              {message.text}
            </div>
          )}

          {isLoading ? (
            <p>Loading settings...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="intervalDays"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reminder Interval (Days)
                </label>
                <input
                  id="intervalDays"
                  type="number"
                  min="1"
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(parseInt(e.target.value))}
                  className="input"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  How many days to wait between sending reminder emails
                </p>
              </div>

              <div>
                <label
                  htmlFor="maxReminders"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Maximum Reminders
                </label>
                <input
                  id="maxReminders"
                  type="number"
                  min="1"
                  value={maxReminders}
                  onChange={(e) => setMaxReminders(parseInt(e.target.value))}
                  className="input"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Maximum number of reminders to send per resident
                </p>
              </div>

              <div>
                <label
                  htmlFor="tokenExpirationDays"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Token Expiration (Days)
                </label>
                <input
                  id="tokenExpirationDays"
                  type="number"
                  min="1"
                  value={tokenExpirationDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value > 0) {
                      setTokenExpirationDays(value);
                    }
                  }}
                  className="input"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Number of days before a resident's form link expires
                </p>
              </div>

              <div>
                <label
                  htmlFor="retentionDays"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Campaign Retention (Days)
                </label>
                <input
                  id="retentionDays"
                  type="number"
                  min="1"
                  value={retentionDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value > 0) {
                      setRetentionDays(value);
                    }
                  }}
                  className="input"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Number of days to keep stopped campaigns before automatic cleanup
                </p>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn btn-primary"
                >
                  {updateMutation.isPending ? "Saving..." : "Save Settings"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/admin/dashboard")}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold mb-4 text-red-600">Debug & Diagnostics</h3>
            <p className="text-sm text-gray-600 mb-4">
              Use this tool to verify that Sentry error tracking is correctly configured. Clicking
              the button below will trigger a deliberate error.
            </p>
            <button
              onClick={() => {
                Sentry.captureMessage("Sentry test message from Settings page");
                throw new Error("This is your first error! - Sentry Test");
              }}
              className="btn bg-red-600 hover:bg-red-700 text-white"
            >
              Test Sentry Configuration (Break the world)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
